import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getItem,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert, warningAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import AssetSubtype, { newAssetSubtype } from "../../models/asset-subtype";
import AssetTypeSelect from "../asset-type/AssetTypeSelect";
import AssetType from "../../models/asset-type";

interface AssetSubtypeFormProps {
  initialAssetSubtype?: AssetSubtype;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const AssetSubtypeForm: React.FC<AssetSubtypeFormProps> = ({
  initialAssetSubtype,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingAssetSubtype, setEditingAssetSubtype] = useState<AssetSubtype>(
    initialAssetSubtype ? initialAssetSubtype : newAssetSubtype()
  );
  const [selectedAssetType, setSelectedAssetType] = useState<
    AssetType | undefined
  >(undefined);
  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingAssetSubtype({
      ...editingAssetSubtype,
      name: e.target.value,
    });
  };

  const onAssetTypeChange = (newAssetType: AssetType | null) => {
    setSelectedAssetType(newAssetType !== null ? newAssetType : undefined);
    setEditingAssetSubtype({
      ...editingAssetSubtype,
      typeId: newAssetType !== null ? newAssetType.id : undefined,
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    let toSendAssetSubtype = {
      ...editingAssetSubtype,
      companyId: company.id,
    };
    let requestPromise: Promise<ItemRequestStatus<AssetSubtype>>;
    if (editingAssetSubtype.id === undefined) {
      requestPromise = createItem<AssetSubtype>(
        "/asset_subtypes/create/",
        toSendAssetSubtype
      );
    } else {
      requestPromise = updateItem<AssetSubtype>(
        `/asset_subtypes/${toSendAssetSubtype.id}/`,
        toSendAssetSubtype
      );
    }

    const assetSubtypeStatus = await requestPromise;

    if (assetSubtypeStatus.status !== SUCCESS) {
      if (assetSubtypeStatus.errors !== undefined) {
        setErrors(assetSubtypeStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (assetSubtypeStatus.detail !== undefined) {
        message = assetSubtypeStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingAssetSubtype(newAssetSubtype());
    setSelectedAssetType(undefined);
  };

  const fetchAssetType = useCallback(
    async (id: number) => {
      const typeStatus = await getItem<AssetType>(`/asset_types/${id}/`);
      if (typeStatus.status === SUCCESS) {
        if (typeStatus.data !== undefined) {
          setSelectedAssetType(typeStatus.data);
        }
      } else {
        const message = typeStatus.detail
          ? typeStatus.detail
          : "Error desconocido";
        warningAlert(message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialAssetSubtype]
  );

  useEffect(() => {
    setEditingAssetSubtype(
      initialAssetSubtype ? initialAssetSubtype : newAssetSubtype()
    );
    if (initialAssetSubtype?.typeId) {
      fetchAssetType(initialAssetSubtype.typeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAssetSubtype]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nombre:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingAssetSubtype.name)}
                onChange={onNameChange}
                placeholder="Nombre"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("name", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Tipo de Productor Padre:</CLabel>
            </CCol>
            <CCol>
              <AssetTypeSelect
                value={selectedAssetType ? selectedAssetType : null}
                onChange={onAssetTypeChange}
              ></AssetTypeSelect>
              <FieldErrors
                errors={getFieldErrors("typeId", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default AssetSubtypeForm;
